import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import { useRegistrationFunnel } from '../hooks/useFunnelAnalysis.js';
import type { FunnelStepResult } from '../types/funnel-analysis.js';
import { TOOLTIP_CONTENT_STYLE, GRID_PROPS } from '../lib/chart-theme.js';
import { DateRangeFilter } from '../components/chart/DateRangeFilter.js';

/**
 * ステップカラーパレット。
 * @graph-connects none
 */
const STEP_COLORS = ['#0017C1', '#1A3FDB', '#3460FB', '#5080F8', '#7096F8'];

/** @graph-connects none */
function formatPercent(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '-';
  return `${value.toFixed(1)}%`;
}

/** @graph-connects none */
function formatNumber(value: number): string {
  return value.toLocaleString('ja-JP');
}

/** @graph-connects none */
interface FunnelBarDatum {
  label: string;
  visitors: number;
  fill: string;
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business ファネル分析ページ。UX Insights BQ データからメンズ正規登録導線（LP→会員登録→サブスク）のファネルを可視化する。期間合計の横棒ファネルチャートと日次推移の折れ線グラフを表示する。
 * @graph-connects useRegistrationFunnel [calls] 登録ファネル取得フック
 */
export function FunnelAnalysisPage() {
  const { t } = useTranslation();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const query = useRegistrationFunnel(from || undefined, to || undefined);

  const summary = query.data?.summary ?? [];
  const isAllZero = summary.length > 0 && summary.every((s) => s.uniqueVisitors === 0);

  const funnelData = useMemo<FunnelBarDatum[]>(() => {
    if (summary.length === 0) return [];
    return summary.map((step, i) => ({
      label: step.label,
      visitors: step.uniqueVisitors,
      fill: STEP_COLORS[i % STEP_COLORS.length],
    }));
  }, [summary]);

  const dailyChartData = useMemo(() => {
    const daily = query.data?.daily ?? [];
    return daily.map((row) => {
      const entry: Record<string, string | number> = { date: row.date };
      for (const step of row.steps) {
        entry[step.key] = step.uniqueVisitors;
      }
      return entry;
    });
  }, [query.data]);

  const stepKeys = summary.map((s) => s.key);

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8">
      <div className="mb-1 font-mono text-xs tracking-widest text-[var(--color-accent-primary)] uppercase">
        Funnel Analysis
      </div>
      <h1 className="mb-6 text-2xl font-bold text-[var(--color-text-primary)]">
        {t('funnelAnalysis.title')}
      </h1>

      <div className="mb-6">
        <DateRangeFilter from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
      </div>

      {query.isLoading && (
        <div className="flex justify-center py-12">
          <div className="loading-spinner" />
        </div>
      )}

      {query.isError && (
        <Card className="p-6 text-center text-[var(--color-error)]">{t('common.fetchError')}</Card>
      )}

      {query.data && isAllZero && (
        <Card className="p-6 text-center text-[var(--color-text-secondary)]">
          {t('funnelAnalysis.noData')}
        </Card>
      )}

      {query.data && !isAllZero && (
        <>
          <FunnelSummarySection summary={summary} funnelData={funnelData} />
          <StepConversionTable summary={summary} />
          <DailyTrendSection
            dailyChartData={dailyChartData}
            stepKeys={stepKeys}
            summary={summary}
          />
        </>
      )}
    </div>
  );
}

/** @graph-connects none */
function FunnelSummarySection({
  summary,
  funnelData,
}: {
  summary: FunnelStepResult[];
  funnelData: FunnelBarDatum[];
}) {
  const { t } = useTranslation();
  if (summary.length === 0) return null;

  return (
    <Card className="mb-6 p-6">
      <h2 className="mb-4 text-lg font-semibold text-[var(--color-text-primary)]">
        {t('funnelAnalysis.summaryTitle')}
      </h2>
      <div className="h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={funnelData} layout="vertical" margin={{ left: 160, right: 60 }}>
            <CartesianGrid {...GRID_PROPS} />
            <XAxis type="number" tick={{ fill: 'var(--color-text-secondary)', fontSize: 12 }} />
            <YAxis
              type="category"
              dataKey="label"
              width={150}
              tick={{ fill: 'var(--color-text-primary)', fontSize: 12 }}
            />
            <Tooltip
              contentStyle={TOOLTIP_CONTENT_STYLE}
              formatter={(value) => [formatNumber(Number(value)), t('funnelAnalysis.visitors')]}
            />
            <Bar dataKey="visitors" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

/** @graph-connects none */
function StepConversionTable({ summary }: { summary: FunnelStepResult[] }) {
  const { t } = useTranslation();
  if (summary.length === 0) return null;
  const topVisitors = summary[0].uniqueVisitors;

  return (
    <Card className="mb-6 overflow-x-auto p-6">
      <h2 className="mb-4 text-lg font-semibold text-[var(--color-text-primary)]">
        {t('funnelAnalysis.stepConversion')}
      </h2>
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className="pb-2 pr-4 font-medium text-[var(--color-text-secondary)]">
              {t('funnelAnalysis.step')}
            </th>
            <th className="pb-2 pr-4 text-right font-medium text-[var(--color-text-secondary)]">
              {t('funnelAnalysis.visitors')}
            </th>
            <th className="pb-2 pr-4 text-right font-medium text-[var(--color-text-secondary)]">
              {t('funnelAnalysis.overallCvr')}
            </th>
            <th className="pb-2 text-right font-medium text-[var(--color-text-secondary)]">
              {t('funnelAnalysis.stepCvr')}
            </th>
          </tr>
        </thead>
        <tbody>
          {summary.map((step, i) => {
            const overallCvr = topVisitors > 0 ? (step.uniqueVisitors / topVisitors) * 100 : null;
            return (
              <tr key={step.key} className="border-b border-[var(--color-border)] last:border-0">
                <td className="py-2.5 pr-4">
                  <div className="flex items-center gap-2">
                    <div
                      className="h-3 w-3 rounded-sm"
                      style={{ backgroundColor: STEP_COLORS[i % STEP_COLORS.length] }}
                    />
                    <span className="text-[var(--color-text-primary)]">{step.label}</span>
                  </div>
                </td>
                <td className="py-2.5 pr-4 text-right font-mono text-[var(--color-text-primary)]">
                  {formatNumber(step.uniqueVisitors)}
                </td>
                <td className="py-2.5 pr-4 text-right font-mono text-[var(--color-text-secondary)]">
                  {formatPercent(overallCvr)}
                </td>
                <td className="py-2.5 text-right font-mono text-[var(--color-text-secondary)]">
                  {formatPercent(step.conversionRate)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

/** @graph-connects none */
function DailyTrendSection({
  dailyChartData,
  stepKeys,
  summary,
}: {
  dailyChartData: Record<string, string | number>[];
  stepKeys: string[];
  summary: FunnelStepResult[];
}) {
  const { t } = useTranslation();
  if (dailyChartData.length === 0) return null;

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold text-[var(--color-text-primary)]">
        {t('funnelAnalysis.dailyTrend')}
      </h2>
      <div className="h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={dailyChartData} margin={{ left: 20, right: 20 }}>
            <CartesianGrid {...GRID_PROPS} />
            <XAxis
              dataKey="date"
              tick={{ fill: 'var(--color-text-secondary)', fontSize: 11 }}
              tickFormatter={(v: string) => v.slice(5)}
            />
            <YAxis tick={{ fill: 'var(--color-text-secondary)', fontSize: 12 }} />
            <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} />
            <Legend />
            {stepKeys.map((key, i) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                name={summary[i]?.label ?? key}
                stroke={STEP_COLORS[i % STEP_COLORS.length]}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
